/**
 * Equipment to Maintenance Job Linking Utility
 *
 * Automatically links equipment items to their required maintenance jobs
 * based on equipment type matching with job.equipmentLinks
 */

import { getJobFrequencyDays } from './intervalHelpers';

/**
 * Equipment type matching patterns
 * Maps equipment identifiers to their type categories
 */
const EQUIPMENT_TYPE_PATTERNS = {
  // Fire Extinguishers
  extinguisher: [
    'fire_extinguisher',
    'extinguisher',
    'Fire Extinguisher',
    'Portable Fire Extinguisher'
  ],

  // Fire Buckets
  bucket: [
    'fire_bucket',
    'bucket',
    'Fire Bucket'
  ],

  // Lifebuoys
  lifebuoy: [
    'lifebuoy',
    'life_buoy',
    'Lifebuoy',
    'Lifebuoy with Line'
  ],

  // Lifejackets
  lifejacket: [
    'lifejacket',
    'life_jacket',
    'Lifejacket',
    'Lifejacket (Adult)',
    'Lifejacket (Child)'
  ],

  // Liferafts
  liferaft: [
    'liferaft',
    'life_raft',
    'Liferaft',
    'Inflatable Liferaft'
  ],

  // Flares
  flare: [
    'flare',
    'distress_flare',
    'Red Hand Flare',
    'Parachute Flare',
    'Orange Smoke',
    'Distress Flare'
  ]
};

/**
 * Check if an equipment item matches a type pattern
 */
function matchesEquipmentType(equipment, typeKey) {
  const patterns = EQUIPMENT_TYPE_PATTERNS[typeKey];
  if (!patterns) return false;

  const equipmentType = equipment.type || equipment.label || equipment.name || '';
  const equipmentCategory = equipment.category || '';

  return patterns.some(pattern => {
    const lowerPattern = pattern.toLowerCase();
    const lowerType = equipmentType.toLowerCase();
    const lowerCategory = equipmentCategory.toLowerCase();

    return lowerType.includes(lowerPattern) ||
           lowerCategory.includes(lowerPattern) ||
           lowerType === pattern ||
           equipmentType === pattern;
  });
}

/**
 * Link equipment items to maintenance jobs
 *
 * @param {Array} equipmentItems - Array of equipment from register
 * @param {Array} maintenanceJobs - Array of maintenance requirement jobs
 * @returns {Array} Equipment items with linkedJobIds added
 */
export function linkEquipmentToJobs(equipmentItems, maintenanceJobs) {
  return equipmentItems.map(equipment => {
    // Find all jobs that this equipment should be linked to
    const linkedJobIds = maintenanceJobs
      .filter(job => {
        if (!job.equipmentLinks || !Array.isArray(job.equipmentLinks)) {
          return false;
        }

        // Check if equipment matches any of the job's equipment links
        return job.equipmentLinks.some(linkType =>
          matchesEquipmentType(equipment, linkType)
        );
      })
      .map(job => job.id);

    return {
      ...equipment,
      linkedJobIds: linkedJobIds.length > 0 ? linkedJobIds : (equipment.linkedJobIds || [])
    };
  });
}

/**
 * Get all jobs linked to a specific equipment item
 *
 * @param {Object} equipment - Equipment item
 * @param {Array} maintenanceJobs - Array of maintenance jobs
 * @returns {Array} Array of linked jobs
 */
export function getLinkedJobs(equipment, maintenanceJobs) {
  if (!equipment.linkedJobIds || equipment.linkedJobIds.length === 0) {
    return [];
  }

  return maintenanceJobs.filter(job =>
    equipment.linkedJobIds.includes(job.id)
  );
}

/**
 * Calculate compliance status based on linked jobs
 *
 * @param {Object} equipment - Equipment item
 * @param {Array} maintenanceJobs - Array of maintenance jobs
 * @param {Object} maintenanceHistory - Maintenance completion history
 * @returns {Object} { status: 'ok'|'due_soon'|'overdue'|'unknown', worstJob: job|null }
 */
export function calculateEquipmentStatus(equipment, maintenanceJobs, maintenanceHistory = {}) {
  const linkedJobs = getLinkedJobs(equipment, maintenanceJobs);

  if (linkedJobs.length === 0) {
    // Fall back to simple lastServiceAt logic
    if (!equipment.lastServiceAt) {
      return { status: 'unknown', worstJob: null };
    }

    const lastService = new Date(equipment.lastServiceAt);
    const daysSinceService = Math.floor((new Date() - lastService) / (1000 * 60 * 60 * 24));

    if (daysSinceService > 365) {
      return { status: 'overdue', worstJob: null };
    } else if (daysSinceService > 335) {
      return { status: 'due_soon', worstJob: null };
    } else {
      return { status: 'ok', worstJob: null };
    }
  }

  // Check status of all linked jobs
  let worstStatus = 'ok';
  let worstJob = null;

  linkedJobs.forEach(job => {
    const lastCompleted = maintenanceHistory[job.id];

    if (!lastCompleted) {
      // Never completed - treat as overdue if mandatory
      if (job.mandatory) {
        worstStatus = 'overdue';
        worstJob = job;
      }
      return;
    }

    const lastDate = new Date(lastCompleted);
    const now = new Date();
    const intervalDays = getJobFrequencyDays(job);

    const daysSince = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
    const daysUntilDue = intervalDays - daysSince;

    let jobStatus = 'ok';
    if (daysUntilDue < 0) {
      jobStatus = 'overdue';
    } else if (daysUntilDue < 30) {
      jobStatus = 'due_soon';
    }

    // Update worst status
    if (jobStatus === 'overdue' ||
        (jobStatus === 'due_soon' && worstStatus !== 'overdue')) {
      worstStatus = jobStatus;
      worstJob = job;
    }
  });

  return { status: worstStatus, worstJob };
}

/**
 * Get equipment items that need a specific job
 *
 * @param {String} jobId - Maintenance job ID
 * @param {Array} equipmentItems - Array of equipment items
 * @returns {Array} Equipment items linked to this job
 */
export function getEquipmentForJob(jobId, equipmentItems) {
  return equipmentItems.filter(equipment =>
    equipment.linkedJobIds && equipment.linkedJobIds.includes(jobId)
  );
}

/**
 * Add a new equipment type pattern
 * Useful for custom equipment added by users
 *
 * @param {String} typeKey - Type key (e.g., 'custom_pump')
 * @param {Array} patterns - Array of matching patterns
 */
export function addEquipmentTypePattern(typeKey, patterns) {
  EQUIPMENT_TYPE_PATTERNS[typeKey] = patterns;
}
